<script setup lang="ts">
import { ref, reactive, computed, watch } from 'vue';
import { ElMessage } from 'element-plus';
import { useAdminStore, type Order, type OfflineFreightStatus } from '../stores/admin';

const store = useAdminStore();

const shipmentDialogOpen = ref(false);
const shipmentSubmitting = ref(false);
const shipmentDraft = reactive({
  orderNo: "",
  carrierCode: "JD",
  trackingNo: "",
  isUpdate: false,
});

const supportedCarriers = [
  { code: "JD", name: "京东快递" },
  { code: "SF", name: "顺丰速运" },
  { code: "ZTO", name: "中通快递" },
  { code: "YTO", name: "圆通速递" },
  { code: "YD", name: "韵达速递" },
  { code: "STO", name: "申通快递" },
  { code: "JTSD", name: "极兔速递" },
  { code: "EMS", name: "邮政速递/EMS" },
  { code: "DBL", name: "德邦快递" },
];

const refundDialogOpen = ref(false);
const refundSubmitting = ref(false);
const reviewDialogOpen = ref(false);
const reviewSubmitting = ref(false);
const activeOrder = ref<Order | null>(null);
const refundDraft = reactive({ note: "" });
const reviewDraft = reactive({ decision: "approve" as "approve" | "reject", confirmedRecovery: false, note: "" });

function fulfillmentLabel(type: string) {
  return type === 'PICKUP' ? '到店自提' : '快递配送';
}

function statusTagType(status: string) {
  switch (status) {
    case 'READY_FOR_PICKUP':
    case 'FULFILLING': return 'warning';
    case 'REFUND_REVIEW': return 'danger';
    case 'PAID':
    case 'PROCESSING': return 'primary';
    case 'SHIPPED':
    case 'COMPLETED': return 'success';
    case 'CANCELLED':
    case 'REFUNDED': return 'info';
    case 'REFUNDING': return 'danger';
    default: return 'info';
  }
}

function statusLabel(status: string) {
  const map: Record<string, string> = {
    PENDING_PAYMENT: "待支付", PAID: "待处理", PROCESSING: "处理中",
    READY_FOR_PICKUP: "待自提", SHIPPED: "已发货", COMPLETED: "已完成",
    CANCELLED: "已取消", REFUND_REVIEW: "退款待商家审核", REFUNDING: "退款中", REFUNDED: "已退款",
    FULFILLING: "履约结算处理中"
  };
  return map[status] ?? status;
}

function offlineStatusLabel(value?: OfflineFreightStatus) {
  return value ? ({
    PENDING_CONTACT: "待电话确认", AGREED_AWAITING_QUOTE: "待协商运费",
    PENDING_OFFLINE_PAYMENT: "待微信收款", OFFLINE_PAID: "运费已到账", DECLINED: "已拒绝"
  } as Record<OfflineFreightStatus, string>)[value] : "—";
}

function orderLinesSummary(order: Order) {
  return order.lines?.length ? order.lines.map((line) => `${line.titleSnapshot || line.title} ×${line.quantity}`).join("、") : "";
}

const carrierMap: Record<string, string> = {
  SF: "顺丰速运",
  JD: "京东快递",
  ZTO: "中通快递",
  YTO: "圆通速递",
  YD: "韵达速递",
  STO: "申通快递",
  JTSD: "极兔速递",
  EMS: "邮政速递/EMS",
  DBL: "德邦快递",
};

function carrierLabel(code?: string) {
  return (code && carrierMap[code]) || code || "快递配送";
}

function adjustmentStatusLabel(status: string) {
  return ({ PENDING: "待处理", PROCESSING: "处理中", SUCCEEDED: "已完成", FAILED: "失败可重试", CANCELLED: "已取消" } as Record<string, string>)[status] ?? status;
}

function formatDate(value?: number) {
  return value ? new Date(value).toLocaleString("zh-CN", { hour12: false }) : "—";
}

function openShipment(order: Order, isUpdate = false) {
  activeOrder.value = order;
  shipmentDraft.orderNo = order.orderNo;
  shipmentDraft.isUpdate = isUpdate;
  shipmentDraft.carrierCode = order.shipment?.carrierCode || "JD";
  shipmentDraft.trackingNo = order.shipment?.trackingNo || "";
  shipmentDialogOpen.value = true;
}

function onTrackingInput(val: string) {
  const trimmed = (val || "").trim();
  shipmentDraft.trackingNo = trimmed;
  const upper = trimmed.toUpperCase();
  if (upper.startsWith("SF")) {
    shipmentDraft.carrierCode = "SF";
  } else if (upper.startsWith("JD") || upper.startsWith("JDX") || upper.startsWith("JDL")) {
    shipmentDraft.carrierCode = "JD";
  } else if (upper.startsWith("YT")) {
    shipmentDraft.carrierCode = "YTO";
  }
}

async function submitShipment() {
  const order = activeOrder.value;
  if (!order || shipmentSubmitting.value) return;
  const trackingNo = shipmentDraft.trackingNo.trim();
  if (!trackingNo) {
    return ElMessage.warning("请输入快递运单号");
  }
  shipmentSubmitting.value = true;
  try {
    await store.adminFetch(`/api/admin/orders/${encodeURIComponent(order.orderNo)}/ship`, {
      method: "POST",
      body: JSON.stringify({
        carrierCode: shipmentDraft.carrierCode,
        trackingNo,
      }),
    });
    ElMessage.success(shipmentDraft.isUpdate ? "物流信息修改成功，已同步微信" : "发货成功，已同步至微信发货管理");
    shipmentDialogOpen.value = false;
    await store.loadOrders();
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : "发货失败");
  } finally {
    shipmentSubmitting.value = false;
  }
}

async function copyTrackingNo(no?: string) {
  if (!no) return;
  try { await navigator.clipboard?.writeText(no); ElMessage.success("运单号已复制"); }
  catch { ElMessage.warning(`运单号：${no}`); }
}

function openRefund(order: Order) {
  activeOrder.value = order;
  refundDraft.note = "";
  refundDialogOpen.value = true;
}

async function submitRefund() {
  const order = activeOrder.value;
  if (!order || refundSubmitting.value) return;
  refundSubmitting.value = true;
  try {
    await store.requestOrderRefund(order.orderNo, refundDraft.note);
    refundDialogOpen.value = false;
    ElMessage.success("整单商品款退款已提交");
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : "退款提交失败");
  } finally {
    refundSubmitting.value = false;
  }
}

function openReview(order: Order, decision: "approve" | "reject") {
  activeOrder.value = order;
  reviewDraft.decision = decision;
  reviewDraft.confirmedRecovery = false;
  reviewDraft.note = "";
  reviewDialogOpen.value = true;
}

async function submitReview() {
  const order = activeOrder.value;
  if (!order || reviewSubmitting.value) return;
  if (reviewDraft.decision === "approve" && !reviewDraft.confirmedRecovery) return ElMessage.warning("请先确认货物未交运或已成功追回");
  if (reviewDraft.decision === "reject" && !reviewDraft.note.trim()) return ElMessage.warning("请填写拒绝原因，顾客将看到此说明");
  reviewSubmitting.value = true;
  try {
    const path = reviewDraft.decision === "approve" ? "approve" : "reject";
    await store.adminFetch(`/api/admin/orders/${encodeURIComponent(order.orderNo)}/refund-review/${path}`, {
      method: "POST",
      body: JSON.stringify(reviewDraft.decision === "approve"
        ? { confirmedRecovery: true, note: reviewDraft.note.trim() || undefined }
        : { reason: reviewDraft.note.trim() }),
    });
    reviewDialogOpen.value = false;
    ElMessage.success(reviewDraft.decision === "approve" ? "已发起退款，请以微信退款结果为准" : "已拒绝申请，可继续处理订单");
    await store.loadOrders();
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : "退款审核失败");
  } finally {
    reviewSubmitting.value = false;
  }
}

async function copyPhone(phone?: string) {
  if (!phone) return;
  try { await navigator.clipboard?.writeText(phone); ElMessage.success("手机号已复制"); }
  catch { ElMessage.warning(`手机号：${phone}`); }
}

async function copyOrderNo(orderNo: string) {
  try { await navigator.clipboard?.writeText(orderNo); ElMessage.success("商户订单号已复制"); }
  catch { ElMessage.warning(`商户订单号：${orderNo}`); }
}

async function syncShipping(order: Order) {
  try {
    await store.adminFetch(`/api/admin/orders/${encodeURIComponent(order.orderNo)}/wechat-shipping-sync`, { method: "POST" });
    ElMessage.success("物流数据已同步更新");
    await store.loadOrders();
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : "物流同步失败");
  }
}

function formatOrderAmount(cents?: number | null) {
  if (cents == null) return "¥0.00";
  return `¥${store.centsInput(cents)}`;
}

function lineQuantityText(line: { quantity: number; quantityMinor?: number; billingUnit?: string }) {
  const count = line.quantityMinor != null ? (line.quantityMinor / 10).toFixed(1) : String(line.quantity);
  const unit = line.billingUnit === 'JIN' ? '斤' : '件';
  return `${count} ${unit}`;
}

function linePriceText(line: { unitPriceCents?: number; billingUnit?: string }) {
  if (line.unitPriceCents == null) return "";
  const unit = line.billingUnit === 'JIN' ? '斤' : '件';
  return `¥${store.centsInput(line.unitPriceCents)}/${unit}`;
}

function lineTotalText(line: { unitPriceCents?: number; quantity: number; quantityMinor?: number }) {
  if (line.unitPriceCents == null) return "";
  const qty = line.quantityMinor != null ? line.quantityMinor / 10 : line.quantity;
  const total = Math.round(line.unitPriceCents * qty);
  return `¥${store.centsInput(total)}`;
}

async function copyShippingInfo(order: Order) {
  if (!order.deliveryAddress) return;
  const { recipientName, phone, province, city, district, detail } = order.deliveryAddress;
  const text = `${recipientName} ${phone} ${province}${city}${district}${detail}`;
  try {
    await navigator.clipboard?.writeText(text);
    ElMessage.success("完整收件信息已复制，可直接粘贴寄件");
  } catch {
    ElMessage.warning(`收件信息：${text}`);
  }
}

async function copyPackingDetail(order: Order) {
  const parts: string[] = [];

  // 1. 提取收件人/自提人 姓名与电话
  if (order.deliveryAddress) {
    const name = (order.deliveryAddress.recipientName || "").trim();
    const phone = (order.deliveryAddress.phone || getFreightPhone(order) || "").trim();
    const contact = [name, phone].filter(Boolean).join(" ");
    if (contact) {
      parts.push(contact);
    }
  } else if (order.contactPhone || getFreightPhone(order)) {
    const phone = (order.contactPhone || getFreightPhone(order) || "").trim();
    parts.push(`自提：${phone}`);
  }

  // 2. 提取商品明细（格式：・商品名：数量（¥单价/单位））
  if (order.lines && order.lines.length > 0) {
    for (const line of order.lines) {
      const title = line.title || "未知商品";
      const qty = lineQuantityText(line);
      const price = linePriceText(line);
      if (price) {
        parts.push(`・${title}：${qty}（${price}）`);
      } else {
        parts.push(`・${title}：${qty}`);
      }
    }
  } else {
    parts.push("（暂无商品明细）");
  }

  const text = parts.join("\n");
  try {
    await navigator.clipboard?.writeText(text);
    ElMessage.success("打包明细已复制");
  } catch {
    ElMessage.warning(`打包明细：\n${text}`);
  }
}

function getFreightPhone(order: Order): string {
  return (order.buyerContactPhone || order.contactPhone || "").trim();
}

function isSamePhone(phone1?: string, phone2?: string): boolean {
  if (!phone1 || !phone2) return false;
  const clean1 = phone1.replace(/\D/g, "");
  const clean2 = phone2.replace(/\D/g, "");
  return clean1.length > 0 && clean1 === clean2;
}

type OrderFilterTab = "ALL" | "PENDING" | "PICKUP" | "SHIPPED" | "REFUND";
const currentTab = ref<OrderFilterTab>("ALL");

watch(() => store.targetOrderTab, (newTab) => {
  if (newTab) {
    currentTab.value = newTab;
    store.targetOrderTab = "";
  }
}, { immediate: true });

function isOrderInTab(order: Order, tab: OrderFilterTab) {
  if (tab === "ALL") return true;
  if (tab === "PENDING") return ["PAID", "PROCESSING", "REFUND_REVIEW", "FULFILLING"].includes(order.status);
  if (tab === "PICKUP") return order.status === "READY_FOR_PICKUP";
  if (tab === "SHIPPED") return ["SHIPPED", "COMPLETED"].includes(order.status);
  if (tab === "REFUND") {
    return ["REFUND_REVIEW", "REFUNDING", "REFUNDED"].includes(order.status) || Boolean(order.refundReviews?.length) || (order.adjustments ?? []).some((item) => item.type === "REFUND");
  }
  return true;
}

const tabCounts = computed(() => {
  const list = store.orders;
  return {
    ALL: list.length,
    PENDING: list.filter((o) => isOrderInTab(o, "PENDING")).length,
    PICKUP: list.filter((o) => isOrderInTab(o, "PICKUP")).length,
    SHIPPED: list.filter((o) => isOrderInTab(o, "SHIPPED")).length,
    REFUND: list.filter((o) => isOrderInTab(o, "REFUND")).length,
  };
});

const visibleOrdersCount = computed(() => {
  return store.orders.filter((o) => isOrderInTab(o, currentTab.value)).length;
});

function desktopRowClassName({ row }: { row: Order }) {
  return isOrderInTab(row, currentTab.value) ? '' : 'hidden-order-row';
}
</script>

<template>
  <div class="panel">
    <div class="panel-heading">
      <div>
        <p class="eyebrow">订单履约</p>
        <h2>订单管理与履约处理 (共 {{ store.orders.length }} 单)</h2>
      </div>
      <el-button type="default" size="small" @click="store.loadOrders">刷新列表</el-button>
    </div>

    <!-- Order Status Filter Tabs -->
    <div class="order-filter-bar">
      <button
        type="button"
        :class="['filter-tab-pill', { active: currentTab === 'ALL' }]"
        @click="currentTab = 'ALL'"
      >
        全部 <span class="tab-count">{{ tabCounts.ALL }}</span>
      </button>
      <button
        type="button"
        :class="['filter-tab-pill', { active: currentTab === 'PENDING' }]"
        @click="currentTab = 'PENDING'"
      >
        待处理 <span class="tab-count" :class="{ highlight: tabCounts.PENDING > 0 }">{{ tabCounts.PENDING }}</span>
      </button>
      <button
        type="button"
        :class="['filter-tab-pill', { active: currentTab === 'PICKUP' }]"
        @click="currentTab = 'PICKUP'"
      >
        待自提 <span class="tab-count">{{ tabCounts.PICKUP }}</span>
      </button>
      <button
        type="button"
        :class="['filter-tab-pill', { active: currentTab === 'SHIPPED' }]"
        @click="currentTab = 'SHIPPED'"
      >
        已发货 <span class="tab-count">{{ tabCounts.SHIPPED }}</span>
      </button>
      <button
        type="button"
        :class="['filter-tab-pill', { active: currentTab === 'REFUND' }]"
        @click="currentTab = 'REFUND'"
      >
        售后退款 <span class="tab-count" :class="{ danger: store.pendingRefunds > 0 }">{{ tabCounts.REFUND }}</span>
      </button>
    </div>

    <!-- Desktop View -->
    <div class="desktop-only">
      <el-table
        :data="store.orders"
        class="order-card-table"
        :row-class-name="desktopRowClassName"
        v-loading="store.orderLoading"
        empty-text="暂无订单记录"
        style="width: 100%"
      >
        <el-table-column prop="orderNo" label="订单编号" width="160">
          <template #default="scope">
            <div class="desktop-order-cell">
              <div class="desktop-order-no-row">
                <span class="desktop-order-no">{{ scope.row.orderNo }}</span>
                <button type="button" class="order-copy-link" @click="copyOrderNo(scope.row.orderNo)">复制</button>
              </div>
              <button
                type="button"
                class="copy-packing-btn"
                title="复制员工取货打包明细（含客户姓名电话及商品斤数单价）"
                @click="copyPackingDetail(scope.row)"
              >
                一键复制订单明细
              </button>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="履约方式" width="80" align="center">
          <template #default="scope">
            <el-tag :type="scope.row.fulfillmentType === 'PICKUP' ? 'info' : 'warning'" size="small">
              {{ fulfillmentLabel(scope.row.fulfillmentType) }}
            </el-tag>
          </template>
        </el-table-column>

        <!-- Dedicated Structured Goods Column -->
        <el-table-column label="下单商品明细" min-width="180">
          <template #default="scope">
            <div v-if="scope.row.lines?.length" class="desktop-goods-box">
              <div v-for="(line, lIdx) in scope.row.lines" :key="lIdx" class="desktop-goods-item">
                <div class="desktop-goods-item-top">
                  <span class="desktop-goods-title">{{ line.title }}</span>
                  <span class="desktop-goods-qty">× {{ lineQuantityText(line) }}</span>
                </div>
                <div class="desktop-goods-item-sub" v-if="linePriceText(line) || lineTotalText(line)">
                  <span class="desktop-goods-price">{{ linePriceText(line) }}</span>
                  <span class="desktop-goods-total" v-if="lineTotalText(line)">小计 {{ lineTotalText(line) }}</span>
                </div>
              </div>
            </div>
            <div v-else class="lines-text">暂无商品明细</div>
          </template>
        </el-table-column>

        <el-table-column label="总金额" width="80" align="right">
          <template #default="scope">
            <span class="order-price">{{ formatOrderAmount(scope.row.totalCents) }}</span>
          </template>
        </el-table-column>

        <el-table-column label="订单状态" width="112" align="center">
          <template #default="scope">
            <el-tag :type="statusTagType(scope.row.status)" effect="light" size="small">
              {{ statusLabel(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>

        <!-- Recipient & Delivery Info Column -->
        <el-table-column label="收货人/自提信息" min-width="230">
          <template #default="scope">
            <div v-if="scope.row.fulfillmentType === 'EXPRESS' && scope.row.deliveryAddress" class="addr-box">
              <!-- Case A: Recipient and freight contact are identical (or freight phone not specified) -->
              <div v-if="isSamePhone(scope.row.deliveryAddress.phone, getFreightPhone(scope.row)) || !getFreightPhone(scope.row)" class="desktop-contact-row">
                <span class="contact-label-tag merged">收件/运费</span>
                <strong>{{ scope.row.deliveryAddress.recipientName }}</strong>
                <span class="desktop-phone-val">{{ scope.row.deliveryAddress.phone }}</span>
                <a :href="'tel:' + scope.row.deliveryAddress.phone" class="tel-call-link">拨打</a>
                <el-button text size="small" class="contact-copy-btn" @click="copyPhone(scope.row.deliveryAddress.phone)">复制</el-button>
              </div>

              <!-- Case B: Recipient and freight contact are different -->
              <template v-else>
                <div class="desktop-contact-row">
                  <span class="contact-label-tag">收件人</span>
                  <strong>{{ scope.row.deliveryAddress.recipientName }}</strong>
                  <span class="desktop-phone-val">{{ scope.row.deliveryAddress.phone }}</span>
                  <a :href="'tel:' + scope.row.deliveryAddress.phone" class="tel-call-link">拨打</a>
                  <el-button text size="small" class="contact-copy-btn" @click="copyPhone(scope.row.deliveryAddress.phone)">复制</el-button>
                </div>
                <div class="desktop-contact-row desktop-freight-subline">
                  <span class="contact-label-tag freight">运费联系手机号</span>
                  <span class="desktop-phone-val">{{ getFreightPhone(scope.row) }}</span>
                  <a :href="'tel:' + getFreightPhone(scope.row)" class="tel-call-link">拨打</a>
                  <el-button text size="small" class="contact-copy-btn" @click="copyPhone(getFreightPhone(scope.row))">复制</el-button>
                </div>
              </template>

              <div class="addr-text">
                <span class="addr-label">地址：</span>{{ scope.row.deliveryAddress.province }}{{ scope.row.deliveryAddress.city }}{{ scope.row.deliveryAddress.district }}{{ scope.row.deliveryAddress.detail }}
              </div>

              <div class="desktop-copy-addr-row">
                <button type="button" class="copy-full-addr-btn" @click="copyShippingInfo(scope.row)">
                  一键复制完整收货信息（可直接粘贴寄件）
                </button>
              </div>

              <div v-if="scope.row.shipment?.trackingNo" class="shipment-text">
                {{ carrierLabel(scope.row.shipment.carrierCode) }} · 运单号：{{ scope.row.shipment.trackingNo }}
                <el-button text size="small" class="contact-copy-btn" @click="copyTrackingNo(scope.row.shipment.trackingNo)">复制单号</el-button>
                <span v-if="scope.row.shipment.wechatSyncedAt"> · 已同步微信</span>
              </div>
            </div>

            <div v-else class="pickup-box">
              <div class="desktop-contact-row">
                <span class="contact-label-tag pickup">自提预留</span>
                <span class="desktop-phone-val">{{ scope.row.contactPhone || '未提供' }}</span>
                <template v-if="scope.row.contactPhone">
                  <a :href="'tel:' + scope.row.contactPhone" class="tel-call-link">拨打</a>
                  <el-button text size="small" class="contact-copy-btn" @click="copyPhone(scope.row.contactPhone)">复制</el-button>
                </template>
              </div>
              <div v-if="scope.row.pickupVerification?.code" class="pickup-code-line">
                自提核销码：<span class="pickup-code-val">{{ scope.row.pickupVerification.code }}</span>
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="快捷操作" min-width="165" align="right" fixed="right">
          <template #default="scope">
            <div class="table-actions">
              <!-- 待发货：去发货 -->
              <el-button
                v-if="scope.row.fulfillmentType === 'EXPRESS' && ['PAID', 'PROCESSING'].includes(scope.row.status)"
                type="primary"
                size="small"
                @click="openShipment(scope.row, false)"
              >
                去发货
              </el-button>

              <!-- 已发货或同步中：修改物流 + 同步微信 -->
              <template v-if="scope.row.fulfillmentType === 'EXPRESS' && ['SHIPPED', 'FULFILLING'].includes(scope.row.status)">
                <el-button v-if="scope.row.status === 'SHIPPED'" type="primary" plain size="small" @click="openShipment(scope.row, true)">
                  修改物流
                </el-button>
                <el-button size="small" text @click="syncShipping(scope.row)">
                  同步微信
                </el-button>
              </template>

              <!-- 整单退款 -->
              <el-button
                v-if="['PAID', 'PROCESSING', 'READY_FOR_PICKUP'].includes(scope.row.status)"
                type="danger"
                size="small"
                plain
                @click="openRefund(scope.row)"
              >
                整单退款
              </el-button>

              <template v-if="scope.row.status === 'REFUND_REVIEW'">
                <el-button type="danger" size="small" @click="openReview(scope.row, 'approve')">同意退款</el-button>
                <el-button size="small" @click="openReview(scope.row, 'reject')">拒绝退款</el-button>
              </template>

              <el-button size="small" text @click="copyOrderNo(scope.row.orderNo)">复制商户订单号</el-button>
            </div>
            <div v-if="scope.row.adjustments?.length" class="adjustment-list">
              <div v-for="item in scope.row.adjustments" :key="item.id">
                退款 ¥{{ store.centsInput(item.amountCents) }}（{{ adjustmentStatusLabel(item.status) }}）
                <span class="adjustment-meta">{{ item.note }} · {{ item.createdBy }} · {{ formatDate(item.createdAt) }}</span>
              </div>
            </div>
            <div v-if="scope.row.refundReviews?.length && !(['REFUNDING', 'REFUNDED'].includes(scope.row.status) && scope.row.refundReviews[0]?.status === 'REJECTED')" class="refund-review-line">
              <strong v-if="scope.row.status === 'REFUND_REVIEW'">{{ scope.row.refundReviews[0]?.urgentAt ? '超期订单 · 紧急核对' : '顾客申请 · 待商家审核' }}</strong>
              <span v-else-if="scope.row.refundReviews[0]?.status === 'REJECTED'">{{ scope.row.refundReviews[0]?.source === 'AUTO_TIMEOUT' ? '超期订单已核对' : '退款申请已拒绝' }}：{{ scope.row.refundReviews[0]?.decisionNote }}</span>
              <span v-else-if="scope.row.refundReviews[0]?.status === 'APPROVED'">退款已批准，请核对微信退款结果</span>
              <small>商品款 {{ formatOrderAmount(scope.row.refundReviews[0]?.amountCents) }} · {{ formatDate(scope.row.refundReviews[0]?.createdAt) }}</small>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- Mobile View -->
    <div class="mobile-only mobile-card-list">
      <div v-if="store.orders.length === 0" class="empty-mobile">暂无订单记录</div>
      <div v-else-if="visibleOrdersCount === 0" class="empty-mobile">当前分类下暂无订单</div>
      <div
        v-for="item in store.orders"
        :key="item.orderNo"
        v-show="isOrderInTab(item, currentTab)"
        class="order-card"
      >
        <!-- Header: Order No, Tag, Total Price -->
        <div class="order-header">
          <div class="order-header-left">
            <span class="order-no">{{ item.orderNo }}</span>
            <button type="button" class="order-copy-link" @click="copyOrderNo(item.orderNo)">复制</button>
            <el-tag size="small" :type="statusTagType(item.status)" class="order-status-tag">
              {{ statusLabel(item.status) }}
            </el-tag>
          </div>
          <div class="order-header-right">
            <span class="order-price-label">合计</span>
            <span class="order-price">{{ formatOrderAmount(item.totalCents) }}</span>
          </div>
        </div>

        <!-- Body: Fulfillment & Goods List & Delivery Info -->
        <div class="order-body">
          <div class="order-fulfillment-line">
            <span class="fulfillment-badge" :class="item.fulfillmentType.toLowerCase()">
              {{ fulfillmentLabel(item.fulfillmentType) }}
            </span>
            <span v-if="item.freightMode === 'OFF_PLATFORM_MANUAL'" class="offline-tag">
              线下运费: {{ offlineStatusLabel(item.offlineFreightStatus) }}
            </span>
          </div>

          <!-- Structured Goods Details (The core user request) -->
          <div class="order-goods-box">
            <div class="goods-box-header">
              <div class="goods-box-title-wrap">
                <span class="goods-box-title">下单商品明细</span>
                <span class="goods-count-badge" v-if="item.lines?.length">共 {{ item.lines.length }} 项</span>
              </div>
              <button
                type="button"
                class="copy-packing-btn-mobile"
                @click="copyPackingDetail(item)"
              >
                一键复制订单明细
              </button>
            </div>
            <div v-if="item.lines?.length" class="goods-item-list">
              <div v-for="(line, lIdx) in item.lines" :key="lIdx" class="goods-item-row">
                <div class="goods-item-top">
                  <span class="goods-title">{{ line.title }}</span>
                  <span class="goods-qty">× {{ lineQuantityText(line) }}</span>
                </div>
                <div class="goods-item-bottom">
                  <span class="goods-unit-price" v-if="linePriceText(line)">{{ linePriceText(line) }}</span>
                  <span class="goods-line-total" v-if="lineTotalText(line)">小计 {{ lineTotalText(line) }}</span>
                </div>
              </div>
            </div>
            <div v-else class="goods-item-empty">暂无明细信息</div>
          </div>

          <!-- Contact & Delivery Address Section -->
          <div v-if="item.fulfillmentType === 'EXPRESS' && item.deliveryAddress" class="order-contact-box">
            <!-- Case A: Recipient phone and freight contact phone are identical (or freight phone not specified) -->
            <div v-if="isSamePhone(item.deliveryAddress.phone, getFreightPhone(item)) || !getFreightPhone(item)" class="contact-row">
              <div class="contact-meta">
                <span class="contact-label-tag merged">收件 / 运费联系人</span>
                <span class="contact-name"><strong>{{ item.deliveryAddress.recipientName }}</strong></span>
                <span class="contact-phone-number">{{ item.deliveryAddress.phone }}</span>
              </div>
              <div class="contact-actions">
                <a :href="'tel:' + item.deliveryAddress.phone" class="tel-call-link">拨打</a>
                <el-button text size="small" class="contact-copy-btn" @click="copyPhone(item.deliveryAddress.phone)">复制</el-button>
              </div>
            </div>

            <!-- Case B: Recipient phone and freight contact phone are different -->
            <template v-else>
              <!-- Row 1: Recipient -->
              <div class="contact-row">
                <div class="contact-meta">
                  <span class="contact-label-tag">收件人</span>
                  <span class="contact-name"><strong>{{ item.deliveryAddress.recipientName }}</strong></span>
                  <span class="contact-phone-number">{{ item.deliveryAddress.phone }}</span>
                </div>
                <div class="contact-actions">
                  <a :href="'tel:' + item.deliveryAddress.phone" class="tel-call-link">拨打</a>
                  <el-button text size="small" class="contact-copy-btn" @click="copyPhone(item.deliveryAddress.phone)">复制</el-button>
                </div>
              </div>
              <!-- Row 2: Freight Contact Buyer -->
              <div class="contact-row buyer-freight-row">
                <div class="contact-meta">
                  <span class="contact-label-tag freight">运费联系手机号</span>
                  <span class="contact-phone-number">{{ getFreightPhone(item) }}</span>
                </div>
                <div class="contact-actions">
                  <a :href="'tel:' + getFreightPhone(item)" class="tel-call-link">拨打</a>
                  <el-button text size="small" class="contact-copy-btn" @click="copyPhone(getFreightPhone(item))">复制</el-button>
                </div>
              </div>
            </template>

            <!-- Address Line -->
            <div class="addr-detail-row">
              <span class="contact-label">收件地址：</span>
              <span class="addr-detail-text">{{ item.deliveryAddress.province }}{{ item.deliveryAddress.city }}{{ item.deliveryAddress.district }}{{ item.deliveryAddress.detail }}</span>
            </div>

            <!-- Copy Full Address Button -->
            <div class="addr-quick-action">
              <button type="button" class="copy-full-addr-btn" @click="copyShippingInfo(item)">
                一键复制完整收货信息（可直接粘贴寄件）
              </button>
            </div>
          </div>

          <!-- Pickup order contact -->
          <div v-else class="order-contact-box">
            <div class="contact-row">
              <div class="contact-meta">
                <span class="contact-label-tag pickup">到店自提联系人</span>
                <span class="contact-phone-number" v-if="item.contactPhone">{{ item.contactPhone }}</span>
                <span v-else class="contact-empty">未提供手机号</span>
              </div>
              <div v-if="item.contactPhone" class="contact-actions">
                <a :href="'tel:' + item.contactPhone" class="tel-call-link">拨打</a>
                <el-button text size="small" class="contact-copy-btn" @click="copyPhone(item.contactPhone)">复制</el-button>
              </div>
            </div>
            <div v-if="item.pickupVerification?.code" class="pickup-code-line">
              自提核销码：<span class="pickup-code-val">{{ item.pickupVerification.code }}</span>
            </div>
          </div>

          <!-- Shipment Info -->
          <div v-if="item.shipment?.trackingNo" class="shipment-text">
            {{ carrierLabel(item.shipment.carrierCode) }} · 运单号：{{ item.shipment.trackingNo }}
            <el-button text size="small" class="contact-copy-btn" @click="copyTrackingNo(item.shipment.trackingNo)">复制单号</el-button>
            <span v-if="item.shipment.wechatSyncedAt"> · 已同步微信</span>
          </div>

          <!-- Adjustments / Refunds in Mobile -->
          <div v-if="item.adjustments?.length" class="adjustment-list mobile-adjustments">
            <div v-for="adj in item.adjustments" :key="adj.id" class="adjustment-row">
              退款 ¥{{ store.centsInput(adj.amountCents) }}（{{ adjustmentStatusLabel(adj.status) }}）
              <span class="adjustment-meta">{{ adj.note }} · {{ adj.createdBy }} · {{ formatDate(adj.createdAt) }}</span>
            </div>
          </div>
          <div v-if="item.refundReviews?.length && !(['REFUNDING', 'REFUNDED'].includes(item.status) && item.refundReviews[0]?.status === 'REJECTED')" class="refund-review-line">
            <strong v-if="item.status === 'REFUND_REVIEW'">{{ item.refundReviews[0]?.urgentAt ? '超期订单 · 紧急核对退款' : '顾客退款申请待审核' }}</strong>
            <span v-else-if="item.refundReviews[0]?.status === 'REJECTED'">{{ item.refundReviews[0]?.source === 'AUTO_TIMEOUT' ? '超期订单已核对' : '退款申请已拒绝' }}：{{ item.refundReviews[0]?.decisionNote }}</span>
            <span v-else-if="item.refundReviews[0]?.status === 'APPROVED'">退款已批准，请核对微信退款结果</span>
            <small>商品款 {{ formatOrderAmount(item.refundReviews[0]?.amountCents) }} · {{ formatDate(item.refundReviews[0]?.createdAt) }}</small>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="order-mobile-actions">
          <!-- 待发货：去发货 -->
          <el-button
            v-if="item.fulfillmentType === 'EXPRESS' && ['PAID', 'PROCESSING'].includes(item.status)"
            type="primary"
            size="default"
            @click="openShipment(item, false)"
          >
            去发货
          </el-button>

          <template v-if="item.fulfillmentType === 'EXPRESS' && ['SHIPPED', 'FULFILLING'].includes(item.status)">
            <el-button v-if="item.status === 'SHIPPED'" type="primary" plain size="default" @click="openShipment(item, true)">
              修改物流
            </el-button>
            <el-button size="default" text @click="syncShipping(item)">
              同步微信
            </el-button>
          </template>

          <el-button
            v-if="['PAID', 'PROCESSING', 'READY_FOR_PICKUP'].includes(item.status)"
            type="danger"
            plain
            size="default"
            @click="openRefund(item)"
          >
            整单退款
          </el-button>

          <template v-if="item.status === 'REFUND_REVIEW'">
            <el-button type="danger" size="default" @click="openReview(item, 'approve')">同意退款</el-button>
            <el-button size="default" @click="openReview(item, 'reject')">拒绝退款</el-button>
          </template>

          <el-button size="default" text @click="copyOrderNo(item.orderNo)">复制商户订单号</el-button>
        </div>
      </div>
    </div>

    <!-- Shipment Modal -->
    <el-dialog v-model="shipmentDialogOpen" :title="shipmentDraft.isUpdate ? '修改物流信息' : '订单发货（同步微信）'" width="92%" max-width="520px">
      <div v-if="activeOrder" class="shipment-form">
        <div class="shipment-order-summary">
          <div class="summary-row"><span class="summary-label">订单编号：</span>{{ activeOrder.orderNo }}</div>
          <div class="summary-row" v-if="activeOrder.deliveryAddress">
            <span class="summary-label">收件信息：</span>{{ activeOrder.deliveryAddress.recipientName }} ({{ activeOrder.deliveryAddress.phone }})
          </div>
          <div class="summary-row" v-if="activeOrder.deliveryAddress">
            <span class="summary-label">收货地址：</span>{{ activeOrder.deliveryAddress.province }}{{ activeOrder.deliveryAddress.city }}{{ activeOrder.deliveryAddress.district }}{{ activeOrder.deliveryAddress.detail }}
          </div>
          <div class="summary-row"><span class="summary-label">商品清单：</span>{{ activeOrder.lines?.length ? activeOrder.lines.map(l => `${l.titleSnapshot || l.title} ×${l.quantity}`).join('、') : '暂无商品明细' }}</div>
        </div>

        <el-form label-position="top" class="shipment-edit-form">
          <el-form-item label="快递承运商">
            <el-select v-model="shipmentDraft.carrierCode" placeholder="请选择快递公司" style="width: 100%">
              <el-option v-for="c in supportedCarriers" :key="c.code" :label="c.name" :value="c.code" />
            </el-select>
          </el-form-item>

          <el-form-item label="快递运单号">
            <el-input v-model="shipmentDraft.trackingNo" placeholder="请输入或粘贴快递运单号" clearable @input="onTrackingInput" />
            <div class="input-hint">支持输入 SF/JD 等前缀自动识别快递公司；提交后直连微信小程序官方发货管理。</div>
          </el-form-item>
        </el-form>
      </div>
      <template #footer>
        <el-button @click="shipmentDialogOpen = false">取消</el-button>
        <el-button type="primary" :loading="shipmentSubmitting" @click="submitShipment">
          {{ shipmentDraft.isUpdate ? '保存修改并同步' : '确认发货' }}
        </el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="refundDialogOpen" title="整单退款确认" width="90%" max-width="480px" :close-on-click-modal="!refundSubmitting" :close-on-press-escape="!refundSubmitting">
      <p class="dialog-desc">退款固定为原订单线上微信支付的全部商品款，运费由商家线下协商处理不进入线上退款。退款将原路退回顾客微信支付。</p>
      <el-form label-position="top">
        <el-form-item label="退款金额（商品款）">
          <span class="order-price">{{ formatOrderAmount(activeOrder?.goodsTotalCents ?? activeOrder?.totalCents) }}</span>
          <span class="refund-fixed-hint">（全额商品款退款，不可编辑金额）</span>
        </el-form-item>
        <el-form-item label="电话沟通备注（可选）">
          <el-input v-model="refundDraft.note" type="textarea" rows="3" placeholder="记录退款原因或电话沟通细节（可选）" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="refundDialogOpen = false">取消</el-button>
        <el-button type="danger" :loading="refundSubmitting" @click="submitRefund">确认整单退款</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="reviewDialogOpen" :title="reviewDraft.decision === 'approve' ? '同意快递订单退款' : '拒绝快递订单退款'" width="90%" max-width="480px" :close-on-click-modal="!reviewSubmitting" :close-on-press-escape="!reviewSubmitting">
      <p class="dialog-desc">订单 {{ activeOrder?.orderNo }} · 全额商品款 {{ formatOrderAmount(activeOrder?.goodsTotalCents ?? activeOrder?.totalCents) }}</p>
      <template v-if="reviewDraft.decision === 'approve'">
        <p class="dialog-desc">请先与顾客核实。若货物已交给快递，须确认拦截或追回成功后再退款；点击同意会发起微信退款。</p>
        <el-checkbox v-model="reviewDraft.confirmedRecovery">我已确认货物未交运或已成功追回</el-checkbox>
        <el-input v-model="reviewDraft.note" class="review-note" type="textarea" :rows="2" maxlength="500" show-word-limit placeholder="电话沟通备注（可选）" />
      </template>
      <template v-else>
        <p class="dialog-desc">拒绝后可继续发货；顾客会看到拒绝原因并可拨打店铺电话。</p>
        <el-input v-model="reviewDraft.note" type="textarea" :rows="3" maxlength="500" show-word-limit placeholder="填写拒绝原因（必填）" />
      </template>
      <template #footer>
        <el-button @click="reviewDialogOpen = false">取消</el-button>
        <el-button :type="reviewDraft.decision === 'approve' ? 'danger' : 'primary'" :loading="reviewSubmitting" @click="submitReview">
          {{ reviewDraft.decision === 'approve' ? '确认并发起退款' : '确认拒绝' }}
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped>
.refund-review-line {
  margin-top: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  background: #fff7ed;
  color: #9a3412;
  text-align: left;
  font-size: 12px;
}
.refund-review-line strong,
.refund-review-line small { display: block; }
.refund-review-line small { margin-top: 3px; color: #78350f; }
.review-note { margin-top: 16px; }
.shipment-form {
  padding: 4px 0;
}
.shipment-order-summary {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
  font-size: 13px;
  line-height: 1.6;
}
.summary-row {
  margin-bottom: 4px;
  color: #334155;
}
.summary-row:last-child {
  margin-bottom: 0;
}
.summary-label {
  color: #64748b;
  font-weight: 500;
}
.input-hint {
  font-size: 12px;
  color: #94a3b8;
  margin-top: 4px;
  line-height: 1.4;
}
.order-price {
  font-weight: bold;
  color: #f56c6c;
}
.refund-fixed-hint {
  color: #909399;
  font-size: 12px;
  margin-left: 8px;
}
.addr-box, .pickup-box {
  font-size: 13px;
  line-height: 1.5;
}
.addr-text {
  color: #606266;
  font-size: 12px;
  word-break: break-all;
  overflow-wrap: anywhere;
  line-height: 1.45;
  margin-top: 4px;
}
.lines-text {
  color: #909399;
  font-size: 11px;
}
.shipment-text {
  margin-top: 6px;
  color: #16759a;
  font-size: 12px;
  line-height: 1.45;
  word-break: break-all;
  background: #f0f8fb;
  padding: 6px 10px;
  border-radius: 4px;
}
.shipping-conflict {
  margin-top: 6px;
  color: #c45656;
  font-size: 12px;
  line-height: 1.45;
  font-weight: 600;
  background: #fef0f0;
  padding: 6px 10px;
  border-radius: 4px;
}
.official-shipping-hint {
  max-width: 230px;
  color: #909399;
  font-size: 12px;
  line-height: 1.45;
  text-align: left;
}
.table-actions {
  display: flex;
  gap: 6px;
  justify-content: flex-end;
  flex-wrap: wrap;
}

/* Order Filter Bar */
.order-filter-bar {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  overflow-x: auto;
  padding-bottom: 4px;
  -webkit-overflow-scrolling: touch;
}
.filter-tab-pill {
  flex: 0 0 auto;
  border: 1px solid #dcdfe6;
  background: #fff;
  color: #606266;
  padding: 6px 14px;
  border-radius: 18px;
  font-size: 13px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  white-space: nowrap;
}
.filter-tab-pill:hover {
  border-color: #c6e2ff;
  color: #409eff;
}
.filter-tab-pill.active {
  background: #409eff;
  border-color: #409eff;
  color: #fff;
  font-weight: 600;
}
.tab-count {
  display: inline-block;
  font-size: 11px;
  padding: 1px 6px;
  border-radius: 10px;
  background: #f2f3f5;
  color: #909399;
}
.filter-tab-pill.active .tab-count {
  background: rgba(255, 255, 255, 0.25);
  color: #fff;
}
.tab-count.highlight {
  background: #fdf6ec;
  color: #e6a23c;
  font-weight: 700;
}
.filter-tab-pill.active .tab-count.highlight {
  background: rgba(255, 255, 255, 0.35);
  color: #fff;
}
.tab-count.danger {
  background: #fef0f0;
  color: #f56c6c;
  font-weight: 700;
}
.filter-tab-pill.active .tab-count.danger {
  background: rgba(255, 255, 255, 0.35);
  color: #fff;
}

/* Desktop Table & Common Layout */
.desktop-only {
  width: 100%;
  background: #f6f8fb;
  padding: 8px 10px;
  border-radius: 10px;
  box-sizing: border-box;
}

/* Desktop Order Table Card Layout */
:deep(.order-card-table.el-table) {
  background: transparent !important;
  --el-table-bg-color: transparent !important;
  --el-table-tr-bg-color: transparent !important;
  --el-table-header-bg-color: #eef2f7 !important;
  --el-table-border-color: transparent !important;
}

:deep(.order-card-table.el-table::before),
:deep(.order-card-table .el-table__inner-wrapper::before) {
  display: none !important;
}

/* 核心：订单卡片之间通过 border-spacing 留出 14px 灰色隔离间距 */
:deep(.order-card-table .el-table__body) {
  border-spacing: 0 14px !important;
  border-collapse: separate !important;
}

:deep(.order-card-table th.el-table__cell) {
  background: #eef2f7 !important;
  color: #4a5568;
  font-weight: 600;
  font-size: 13px;
  border-bottom: 1px solid #dcdfe6 !important;
  padding: 10px 8px !important;
}

:deep(.order-card-table .el-table-fixed-column--right) {
  background-color: #ffffff !important;
}
:deep(.order-card-table th.el-table-fixed-column--right) {
  background-color: #eef2f7 !important;
}

/* 每个订单行：纯白立体浮起卡片 */
:deep(.order-card-table tr.el-table__row > td.el-table__cell) {
  background-color: #ffffff !important;
  border-top: 1px solid #e2e8f0 !important;
  border-bottom: 1px solid #e2e8f0 !important;
  border-left: none !important;
  border-right: none !important;
  padding: 14px 8px !important;
  vertical-align: top !important; /* 关键：多商品时整行所有列顶部对齐 */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease;
}

/* 卡片最左列：圆角与蓝色品牌标识条 */
:deep(.order-card-table tr.el-table__row > td.el-table__cell:first-child) {
  border-left: 4px solid #409eff !important;
  border-top-left-radius: 8px !important;
  border-bottom-left-radius: 8px !important;
}

/* 卡片最右列：圆角与右边框 */
:deep(.order-card-table tr.el-table__row > td.el-table__cell:last-child) {
  border-right: 1px solid #e2e8f0 !important;
  border-top-right-radius: 8px !important;
  border-bottom-right-radius: 8px !important;
}

/* 悬停高亮浮起反馈 */
:deep(.order-card-table tr.el-table__row:hover > td.el-table__cell) {
  background-color: #fbfdff !important;
  border-color: #b3d8ff !important;
  box-shadow: 0 4px 14px rgba(64, 158, 255, 0.12) !important;
}

:deep(.order-card-table tr.hidden-order-row) {
  display: none !important;
}

:deep(.el-table .cell) {
  padding-left: 8px;
  padding-right: 8px;
}
.desktop-order-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}
.desktop-order-no-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.desktop-order-no {
  font-family: monospace, sans-serif;
  font-weight: 600;
  font-size: 13px;
  color: #303133;
  word-break: break-all;
}
.order-copy-link {
  border: none;
  background: #f0f2f5;
  color: #409eff;
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s ease;
}
.order-copy-link:hover {
  background: #e1edfa;
}
.order-copy-link:active {
  background: #d9ecff;
}

.copy-packing-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 500;
  color: #409eff;
  background: #ecf5ff;
  border: 1px solid #b3d8ff;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}
.copy-packing-btn:hover {
  background: #409eff;
  color: #ffffff;
  border-color: #409eff;
  box-shadow: 0 2px 6px rgba(64, 158, 255, 0.25);
}
.copy-packing-btn:active {
  background: #337ecc;
  border-color: #337ecc;
}

/* Desktop Goods Box */
.desktop-goods-box {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 4px 0;
}
.desktop-goods-item {
  border-bottom: 1px dashed #ebeef5;
  padding-bottom: 4px;
}
.desktop-goods-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}
.desktop-goods-item-top {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
}
.desktop-goods-title {
  font-weight: 600;
  font-size: 13px;
  color: #303133;
}
.desktop-goods-qty {
  font-weight: 700;
  color: #185384;
  font-size: 13px;
  white-space: nowrap;
}
.desktop-goods-item-sub {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #909399;
  margin-top: 2px;
}
.desktop-goods-price {
  color: #909399;
}
.desktop-goods-total {
  color: #e65d5d;
  font-weight: 600;
}

/* Contact Badges & Buttons (Common for Desktop & Mobile) */
.contact-label-tag {
  display: inline-block;
  font-size: 11px;
  padding: 2px 7px;
  border-radius: 4px;
  font-weight: 500;
  background: #eef4fa;
  color: #3b74a1;
  white-space: nowrap;
}
.contact-label-tag.merged {
  background: #ecf8ec;
  color: #277e2d;
  border: 1px solid #cdeccd;
}
.contact-label-tag.freight {
  background: #fdf6ec;
  color: #b87c24;
  border: 1px solid #faecd8;
}
.contact-label-tag.pickup {
  background: #f4f4f5;
  color: #606266;
  border: 1px solid #e9e9eb;
}
.tel-call-link {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background: #ecf5ff;
  color: #409eff;
  border: 1px solid #b3d8ff;
  border-radius: 4px;
  font-size: 12px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.2s;
}
.tel-call-link:active {
  background: #d9ecff;
}
.contact-copy-btn {
  padding: 2px 8px !important;
  height: auto !important;
  font-size: 12px !important;
  border: 1px solid #dcdfe6 !important;
  border-radius: 4px !important;
  background: #fff !important;
  color: #606266 !important;
}
.contact-copy-btn:active {
  background: #f2f3f5 !important;
}
.copy-full-addr-btn {
  width: 100%;
  background: #f4f8fc;
  color: #2071b7;
  border: 1px dashed #a5c7eb;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 12px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;
  text-align: center;
}
.copy-full-addr-btn:active {
  background: #e3effa;
}
.pickup-code-line {
  margin-top: 6px;
  font-size: 13px;
  color: #303133;
}
.pickup-code-val {
  font-weight: 700;
  color: #409eff;
  font-size: 15px;
  letter-spacing: 1px;
}

/* Desktop Contact Column */
.desktop-contact-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  flex-wrap: wrap;
}
.desktop-phone-val {
  font-size: 14px;
  font-weight: 700;
  color: #1a4f7c;
  font-family: monospace, sans-serif;
}
.desktop-freight-subline {
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px dashed #ebeef5;
}
.desktop-copy-addr-row {
  margin-top: 6px;
}
.addr-label {
  color: #909399;
}

/* Mobile vs Desktop Breakpoint: Upgraded to 1024px for phones and landscape */
.desktop-only { display: block; }
.mobile-only { display: none; }

@media (max-width: 1024px) {
  .desktop-only { display: none; }
  .mobile-only { display: block; }

  .mobile-card-list {
    display: grid;
    gap: 14px;
    max-width: 880px;
    margin: 0 auto;
  }
  .order-card {
    padding: 14px 16px;
    background: #fff;
    border: 1px solid #ebedf0;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
  }
  .order-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #f2f3f5;
    padding-bottom: 10px;
    margin-bottom: 10px;
  }
  .order-header-left {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }
  .order-no {
    font-weight: 700;
    font-size: 13px;
    color: #303133;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, monospace;
  }
  .order-copy-link {
    border: none;
    background: #f0f2f5;
    color: #409eff;
    font-size: 11px;
    padding: 2px 6px;
    border-radius: 4px;
    cursor: pointer;
  }
  .order-copy-link:active {
    background: #d9ecff;
  }
  .order-header-right {
    text-align: right;
    white-space: nowrap;
  }
  .order-price-label {
    font-size: 11px;
    color: #909399;
    margin-right: 4px;
  }
  .order-price {
    font-size: 17px;
    font-weight: 700;
    color: #f56c6c;
  }

  .order-body {
    font-size: 13px;
    color: #606266;
    line-height: 1.5;
  }
  .order-fulfillment-line {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }
  .fulfillment-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
  }
  .fulfillment-badge.express {
    background: #fdf6ec;
    color: #e6a23c;
    border: 1px solid #faecd8;
  }
  .fulfillment-badge.pickup {
    background: #f4f4f5;
    color: #909399;
    border: 1px solid #e9e9eb;
  }
  .offline-tag {
    font-size: 12px;
    color: #e6a23c;
    font-weight: 600;
  }

  /* Structured Goods List */
  .order-goods-box {
    background: #f9fafb;
    border: 1px solid #eef0f3;
    border-radius: 8px;
    padding: 10px 12px;
    margin: 10px 0;
  }
  .goods-box-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 12px;
    color: #606266;
    font-weight: 600;
    margin-bottom: 6px;
    gap: 8px;
  }
  .goods-box-title-wrap {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .copy-packing-btn-mobile {
    font-size: 11px;
    color: #409eff;
    background: #ecf5ff;
    border: 1px solid #b3d8ff;
    padding: 2px 8px;
    border-radius: 4px;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.15s ease;
  }
  .copy-packing-btn-mobile:active {
    background: #409eff;
    color: #fff;
  }
  .goods-count-badge {
    font-size: 11px;
    color: #909399;
    font-weight: normal;
  }
  .goods-item-list {
    display: flex;
    flex-direction: column;
  }
  .goods-item-row {
    padding: 6px 0;
    border-bottom: 1px dashed #e4e7ed;
  }
  .goods-item-row:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
  .goods-item-top {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 8px;
  }
  .goods-title {
    font-weight: 600;
    font-size: 13px;
    color: #303133;
  }
  .goods-qty {
    font-weight: 700;
    font-size: 13px;
    color: #185384;
    white-space: nowrap;
  }
  .goods-item-bottom {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    color: #909399;
    margin-top: 2px;
  }
  .goods-line-total {
    color: #e65d5d;
    font-weight: 600;
  }
  .goods-item-empty {
    font-size: 12px;
    color: #909399;
    padding: 4px 0;
  }

  /* Contact & Delivery Box */
  .order-contact-box {
    background: #fbfcff;
    border: 1px solid #e8edf5;
    border-radius: 8px;
    padding: 10px 12px;
    margin: 10px 0;
  }
  .contact-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
    font-size: 13px;
    flex-wrap: wrap;
    gap: 6px;
  }
  .buyer-freight-row {
    margin-top: 4px;
    padding-top: 6px;
    border-top: 1px dashed #edf1f7;
  }
  .contact-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  .contact-label-tag {
    display: inline-block;
    font-size: 11px;
    padding: 2px 7px;
    border-radius: 4px;
    font-weight: 500;
    background: #eef4fa;
    color: #3b74a1;
    white-space: nowrap;
  }
  .contact-label-tag.merged {
    background: #ecf8ec;
    color: #277e2d;
    border: 1px solid #cdeccd;
  }
  .contact-label-tag.freight {
    background: #fdf6ec;
    color: #b87c24;
    border: 1px solid #faecd8;
  }
  .contact-label-tag.pickup {
    background: #f4f4f5;
    color: #606266;
    border: 1px solid #e9e9eb;
  }
  .contact-name {
    color: #303133;
    font-size: 14px;
  }
  .contact-phone-number {
    font-size: 15px;
    font-weight: 700;
    color: #1a4f7c;
    letter-spacing: 0.5px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, monospace;
  }
  .contact-empty {
    font-size: 12px;
    color: #909399;
  }
  .contact-actions {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-left: auto;
    white-space: nowrap;
  }
  .tel-call-link {
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    background: #ecf5ff;
    color: #409eff;
    border: 1px solid #b3d8ff;
    border-radius: 4px;
    font-size: 12px;
    text-decoration: none;
    font-weight: 600;
    transition: all 0.2s;
  }
  .tel-call-link:active {
    background: #d9ecff;
  }
  .contact-copy-btn {
    padding: 2px 8px !important;
    height: auto !important;
    font-size: 12px !important;
    border: 1px solid #dcdfe6 !important;
    border-radius: 4px !important;
    background: #fff !important;
    color: #606266 !important;
  }
  .contact-copy-btn:active {
    background: #f2f3f5 !important;
  }
  .addr-detail-row {
    display: flex;
    gap: 4px;
    font-size: 12px;
    line-height: 1.45;
    color: #606266;
    margin-top: 6px;
  }
  .addr-detail-text {
    flex: 1;
  }
  .addr-quick-action {
    margin-top: 8px;
  }
  .copy-full-addr-btn {
    width: 100%;
    background: #f4f8fc;
    color: #2071b7;
    border: 1px dashed #a5c7eb;
    border-radius: 6px;
    padding: 7px 10px;
    font-size: 12px;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.2s;
  }
  .copy-full-addr-btn:active {
    background: #e3effa;
  }

  .pickup-code-line {
    margin-top: 6px;
    font-size: 13px;
    color: #303133;
  }
  .pickup-code-val {
    font-weight: 700;
    color: #409eff;
    font-size: 15px;
    letter-spacing: 1px;
  }

  .offline-freight-tip {
    font-size: 11px;
    color: #909399;
    margin-top: 6px;
  }

  .mobile-adjustments {
    margin-top: 8px;
    padding: 8px;
    background: #fdf6ec;
    border-radius: 6px;
    font-size: 12px;
  }
  .adjustment-row {
    color: #c45656;
    margin-bottom: 2px;
  }
  .adjustment-meta {
    display: block;
    color: #909399;
    font-size: 11px;
  }

  /* Actions Area */
  .order-mobile-actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: flex-end;
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px solid #f2f3f5;
  }
  .order-mobile-actions .el-button {
    min-height: 34px;
    padding: 8px 14px;
    font-size: 13px;
  }
  .empty-mobile {
    text-align: center;
    padding: 40px 20px;
    color: #909399;
    font-size: 14px;
  }
}
</style>
